'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Host adapter contract for the LMS surface — catalog rows, course
 * detail, events, news-style articles, categories, and (optionally)
 * enrollments.
 *
 * <p>Hosts implement this against their LMS backend. For our stack
 * the path forward is a public WsEndPoint over
 * {@code com.gobito.akademi.entities.lms.course.LearningDetail} +
 * its supporting entities (categories, providers, coordinators).
 * Today the admin-server only exposes admin DTOs ({@code
 * LearningListView} / {@code LearningDetailView}); the rich detail
 * fields the portal expects (faqs, careerOutcomes, reviewSummary)
 * need to be modelled or denormalised before they reach this
 * contract.
 *
 * <p>Enrollments are an OPTIONAL sub-interface — admin-server has no
 * enrollment entity yet, only {@code PurchasedTraining} (purchase-
 * state, not progress-state). Hosts without enrollment backing leave
 * {@link LearningSource.enrollments} undefined; user-state islands
 * fall back to "Sign in to track progress" placeholders.
 *
 * <p>When no provider is mounted, {@link useLearningSource} returns
 * {@code null}. Blocks should render a "Coming soon" placeholder
 * rather than throwing.
 */

/**
 * Lean catalog item shape. One type serves courses / articles /
 * events / categories — {@link detectLearningItemKind} discriminates
 * at the call site. Every field is optional except {@code id} +
 * {@code title}; blocks read what they need and ignore the rest.
 *
 * <p>Hosts with richer per-item data widen the interface via
 * TypeScript declaration merging.
 */
export interface LearningItem {
  id: string;
  title: string;

  // Generic display
  kicker?: string;
  subtitle?: string;
  imageUrl?: string;
  href?: string;
  meta?: string[];
  badge?: { label: string; tone?: 'brand' | 'success' | 'amber' | 'muted' };

  // Course-specific
  /** Aggregate star rating (0–5) — the backend's {@code ratingAverage}
   *  (average of approved-comment ratings). Same name end-to-end. */
  ratingAverage?: number;
  ratingCount?: number;
  price?: number;
  priceCurrency?: string;
  isBestseller?: boolean;
  progress?: number;
  /** Learner's enrollment state for this course ("REGISTERED",
   *  "COMPLETED", "IN_PROGRESS", …) — cards with a CTA button use it to
   *  pick between "start" / "continue" / "retake" labels. */
  attendeeStatus?: string;
  /** True when enrollment needs operator approval before the learner can
   *  start — cards with a CTA button show "Request" instead of
   *  start/continue/retake. */
  approvalRequired?: boolean;
  instructor?: string;
  instructorAvatar?: string;
  /** Raw trainer name string as the catalog API returns it (may join
   *  multiple trainers) — carried through unmodified alongside the
   *  derived {@code instructor}. */
  trainers?: string;
  /** Trainer user ids from the catalog document — used to backfill
   *  {@code instructorAvatar} via a per-trainer profile lookup for the
   *  feeds that ship no avatar inline (the catalog search response does
   *  carry one on its {@code trainer} object). */
  trainerIds?: number[];
  duration?: string;
  enrolled?: number;
  viewCount?: number;
  commentCount?: number;
  hasCertificate?: boolean;
  certificateBadgeUrl?: string;
  level?: string;
  totalHours?: number;
  outcomes?: string[];
  lastUpdated?: string;
  previewVideoUrl?: string;
  description?: string;
  category?: string;
  categoryName?: string;
  requirements?: string[];
  targetAudience?: string[];
  /** Backend content-type discriminator ("Scorm", "C8Video", "QServExam",
   *  …) — cards that render a per-type glyph key off this. */
  pluginId?: string;
  /** Tenant's resolved {@code accountConfig.domain} (e.g. "HBURADA",
   *  "GBT"). Not per-item data — the host stamps the same tenant-wide
   *  value onto every item — but it travels with the item so the card's
   *  plugin-icon lookup can branch per tenant without a second context. */
  domain?: string;
  /** ISO start/end for a live session (in-class / webinar / meeting
   *  {@code pluginId}s) — cards with a "LIVE" mode use these plus the
   *  current time to show a countdown/badge and gate the "join" CTA.
   *  Distinct from the event-specific {@code startDate}/{@code endDate}
   *  below, which describe a whole event rather than a single course's
   *  live session. */
  liveStartDate?: string;
  liveEndDate?: string;
  /** External meeting URL (Zoom/webinar/etc.) — when set, the "join"
   *  CTA on a live course opens this directly instead of registering
   *  through the backend first. */
  publicJoinUrl?: string;
  chapters?: Array<{
    id?: string;
    title: string;
    lessons?: Array<{
      id?: string;
      title: string;
      duration?: string;
      preview?: boolean;
    }>;
  }>;

  // Article-specific
  date?: string;
  readTime?: string;
  excerpt?: string;

  // Event-specific
  city?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  applyDeadline?: string;
  durationHours?: number;

  // Discriminator
  kind?: string;
}

export type LearningItemKind = 'course' | 'article' | 'event' | 'category';

/**
 * Rich detail returned by {@link LearningSource.fetchCourseDetail}.
 * Strict super-set of {@link LearningItem} for course-specific
 * fields, plus pricing, marketplace flags, reviewSummary,
 * skills/careerOutcomes, etc. — used by the
 * {@code /learn/[courseId]} system page.
 */
export interface LearningCourseDetail {
  id: string;
  title: string;
  tagline?: string;
  category?: string;
  categoryName?: string;

  instructor: LearningInstructorRef;
  /** Full trainer list (the {@code instructor} field above is just the
   *  first one) — hosts whose backend distinguishes multiple trainers
   *  per course populate this; single-trainer hosts can leave it unset. */
  trainers?: LearningInstructorRef[];
  coordinators?: LearningInstructorRef[];

  level: 'Beginner' | 'Intermediate' | 'Advanced';
  /** Raw level/type strings as the host's backend reports them —
   *  distinct from the normalized {@code level} above; some hosts
   *  surface tenant-specific values here that don't map cleanly to the
   *  3-way enum (e.g. a general/public level). */
  learningLevel?: string;
  /** Content-type discriminator as the host's backend reports it (e.g.
   *  "Scorm", "C8Video", "InClass") — distinct from {@code pluginId} on
   *  the card-level shape, kept here for hosts that expose it. */
  learningType?: string;
  format?: 'Self-paced' | 'Cohort' | 'Live';

  hasPreTest?: boolean;
  hasPostTest?: boolean;
  hasEvaluationForm?: boolean;
  /** A preview/sample clip exists before enrolling. */
  hasPreview?: boolean;

  ratingAverage?: number;
  ratingCount?: number;
  enrolled?: number;
  progress?: number;
  likes?: number;
  dislikes?: number;

  price?: number;
  priceCurrency?: string;
  isFree?: boolean;

  isBestseller?: boolean;
  isHot?: boolean;

  duration?: string;
  totalHours?: number;
  totalLectures?: number;
  totalArticles?: number;
  totalDownloads?: number;
  hasMobileAccess?: boolean;
  hasLifetimeAccess?: boolean;
  hasCertificate?: boolean;

  language?: string;
  subtitleLanguages?: string[];
  lastUpdated?: string;

  imageUrl?: string;
  previewVideoUrl?: string;

  description?: string;
  outcomes?: string[];
  requirements?: string[];
  targetAudience?: string[];
  chapters?: LearningChapterDetail[];

  sections?: string[];

  objective?: string;
  summary?: string;
  importantNotice?: string;
  softwareUsed?: Array<{ name: string; iconUrl?: string }>;
  resources?: Array<{
    title: string;
    type: 'pdf' | 'link' | 'code' | 'archive';
    size?: string;
    href?: string;
  }>;
  faqs?: Array<{ question: string; answer: string }>;
  reviewSummary?: {
    distribution: [number, number, number, number, number];
    featured: Array<{
      author: string;
      avatarInitials?: string;
      date: string;
      rating: number;
      text: string;
    }>;
  };
  skills?: Array<{ name: string; category?: string }>;
  careerOutcomes?: {
    salaryRange?: string;
    growthRate?: string;
    typicalRoles?: string[];
    placedCompanies?: string[];
  };
  aiHighlights?: string[];

  kind?: 'course';
}

export interface LearningChapterDetail {
  id?: string;
  title: string;
  lessons?: Array<{
    id?: string;
    title: string;
    duration?: string;
    preview?: boolean;
    progress?: number;
    type?: 'video' | 'article' | 'quiz' | 'project';
    done?: boolean;
  }>;
}

export interface LearningInstructorRef {
  id: string;
  name: string;
  role?: string;
  avatar?: string;
}

/** One FAQ entry, backing {@link LearningSource.fetchCourseFaqs}. */
export interface LearningCourseFaq {
  id?: string;
  question: string;
  answer: string;
}

/** One downloadable course document/resource, backing
 *  {@link LearningSource.fetchCourseDocuments}. {@code url} is a
 *  ready-to-open download link — the host resolves any auth token the
 *  download needs into the URL itself. */
export interface LearningCourseDocument {
  id?: string;
  name: string;
  url?: string;
  mimeType?: string;
  fileSize?: number;
}

/** One learner comment/review, backing {@link LearningSource.fetchCourseComments}. */
export interface LearningCourseComment {
  id?: string;
  author: string;
  authorAvatar?: string;
  /** Whole-star rating (1–5) when the host runs in rating mode. */
  rating?: number;
  text: string;
  createdAt?: string | number;
}

/** Envelope for source-keyed rows (popular_courses, new_releases,
 *  events_upcoming, learning_categories, all_courses, etc.). The
 *  {@code source} key is the URL slug the catalog endpoint accepts;
 *  {@code label} is the localized header text the rail renders. */
export interface LearningRow {
  source: string;
  label: string;
  total: number;
  items: LearningItem[];
  /** True when this row came from a snapshot cache rather than a
   *  fresh response. UI may surface this subtly. */
  stale?: boolean;
}

/** A learning path / "journey" — an ordered bundle of courses the
 *  operator assigns to a learner. Backs the {@code LearningJourneys}
 *  tile grid. Mirrors the backend's
 *  {@code /v1/employee/learning-path/search} document, flattened to
 *  the few fields the tile needs ({@code courses[]} collapsed to a
 *  {@code count}). */
export interface LearningJourneyCourse {
  courseId: string;
  courseTitle: string;
  courseDescription?: string;
  courseThumbnail?: string;
  courseDuration?: number;
  courseType?: string;
  courseLevel?: string;
  pluginId?: string;
  completionPercentage?: number;
  expired?: boolean;
  courseTrainers?: Array<{ id?: number; name?: string; surname?: string; avatarURL?: string }>;
  weight?: number;
}

export interface LearningJourney {
  id: string;
  title: string;
  description?: string;
  /** Number of courses in the path. */
  count?: number;
  /** ISO start/end of the path's availability window, when set. */
  startDate?: string;
  endDate?: string;
  /** Cover image (doc.image.url). */
  imageUrl?: string;
  /** Thumbnail image (doc.thumbnail.url). */
  thumbnailUrl?: string;
  /** Background image URL. */
  backgroundImageUrl?: string;
  /** Semicolon-separated color string from the backend. */
  colors?: string;
  backgroundColor?: string;
  upperBanner?: { id?: number; data?: string; key?: string; alternativeText?: string };
  lowerBanner?: { id?: number; data?: string; key?: string; alternativeText?: string };
  allowSelfRegistration?: boolean;
  attendeeStatus?: string;
  registrationRequired?: boolean;
  weight?: number;
  courses?: LearningJourneyCourse[];
  /** Deep link to the path-detail page. Defaults to
   *  {@code /journeys/{id}} at the block when absent. */
  href?: string;
}

/** LMS-news article (loose shape served by the catalog's
 *  {@code articles} surface). Distinct from {@link Article} on
 *  {@link BlogSource} which is the strict CMS shape. */
export interface LearningArticle {
  id: string;
  title: string;
  excerpt?: string;
  date?: string;
  author?: string;
  category?: string;
  readTime?: string;
  cover?: string;
  body?: string;
  bodyKey?: string;
}

export interface LearningEvent {
  id: string;
  name: string;
  city?: string;
  type?: string;
  level?: string;
  area?: string;
  startDate?: string;
  endDate?: string;
  durationHours?: number;
  code?: string;
  applyDeadline?: string;
  status?: 'upcoming' | 'ongoing' | 'past' | string;
  cover?: string;
  /** Set by the activities BFF — true when the event quota is exhausted. */
  quotaFull?: boolean;
  /** Set by the activities BFF — true when the registration window is closed. */
  entriesClosed?: boolean;
  /** Set by the activities BFF — true when the current user is already registered. */
  registered?: boolean;
}

export interface LearningCategory {
  id: string;
  slug?: string;
  name: string;
  description?: string;
  count?: number;
  imageUrl?: string;
}

/** Rich current-course record returned by the employee course API.
 *  Used by the {@code LastCourse} block's "continue where you left off" UI. */
export interface CurrentCourse {
  id: number;
  courseId: number;
  courseTitle: string;
  courseDescription?: string;
  courseThumbnail?: string;
  completionPercentage: number;
  courseTrainers?: Array<{ id: number; name: string; surname: string; avatarURL?: string }>;
}

/** Enrollment state per course. Mirrors the legacy {@code EnrollmentApi}
 *  shape — admin-server has no enrollment entity yet; the contract
 *  ships in anticipation of that entity landing. */
export interface Enrollment {
  courseSlug: string;
  lastLessonId: string | null;
  progressPercent: number;
  enrolledAt: string;
  lastActiveAt: string;
  completedAt?: string;
}

/** Optional sub-adapter for enrollment read+mutation. Hosts whose
 *  backend doesn't yet model enrollments omit this from their
 *  {@link LearningSource} and consuming islands gracefully fall
 *  back to "sign in to track progress" UI. */
export interface EnrollmentSource {
  list(): Promise<Enrollment[]>;
  enroll(courseSlug: string): Promise<Enrollment | null>;
  updateProgress(
    courseSlug: string,
    args: { lastLessonId?: string; progressPercent?: number },
  ): Promise<Enrollment | null>;
  unenroll(courseSlug: string): Promise<boolean>;
}

/** Faceted filter for {@link LearningSource.searchCourses}. Mirrors the
 *  backend {@code catalog/search} {@code filter} object — every field is
 *  optional so the {@code CourseGrid} block can send only what the page
 *  URL provides. */
export interface CourseSearchFilter {
  /** Catalog document type. Defaults to {@code 'course'} host-side. */
  documentType?: string;
  /** Free-text query (the search bar). */
  searchTerm?: string;
  categoryIds?: number[];
  courseTypes?: string[];
  /** Course-plugin ids — the backend's course-type discriminator
   *  ({@code C8Podcast}, {@code ZoomMeetings}, {@code QServExam}, …).
   *  A block that renders one plugin family (the {@code Podcasts} block,
   *  live-session rails) sends just this. */
  pluginIds?: string[];
  localeIds?: number[];
  courseLevels?: string[];
  demanded?: boolean;
  /** Drop the viewer's already-registered courses from the results.
   *  Driven by the `registered` CategoryTree source (`?skipRegistered=true`). */
  skipRegistered?: boolean;
  /** Restrict results to courses taught by this instructor/trainer user ID. */
  instructorId?: number | string;
}

/** Request shape for {@link LearningSource.searchCourses}. Pagination via
 *  {@code firstResult}/{@code maxResults}; sort via the
 *  {@code sortField}+{@code sortOrder} pair. */
export interface CourseSearchOpts {
  firstResult?: number;
  maxResults?: number;
  sortField?: string;
  sortOrder?: 'ASCENDING' | 'DESCENDING';
  filter?: CourseSearchFilter;
  signal?: AbortSignal;
}

/** Result of a {@link LearningSource.searchCourses} call: the page of
 *  items plus the unpaged total (drives "show more" / result counts). */
export interface CourseSearchResult {
  items: LearningItem[];
  total: number;
}

export interface LearningSource {
  /** Source-keyed rail. Sources are host-defined slugs
   *  (popular_courses, new_releases, events_upcoming,
   *  learning_categories, all_courses, etc.). Returns {@code null}
   *  on miss so blocks can render an empty rail.
   *
   *  <p>{@code offset} pages through the same feed (0 by default) —
   *  drives "load more" / infinite-scroll callers like
   *  {@code CourseRow}. Hosts that don't support paging can ignore it
   *  and always return the first page; the row then simply stops
   *  growing once a repeat page comes back unchanged.
   *
   *  <p>{@code filter} narrows the feed the same way
   *  {@link LearningSource.searchCourses}'s filter does (search term,
   *  category/level/type/locale ids, demanded, skipRegistered) — the
   *  {@code CourseGrid} catalog block sends whatever the page URL carries.
   *  Optional: hosts whose feed endpoint can't apply facets may ignore it
   *  and return the unfiltered feed instead. */
  fetchRow(opts: {
    source: string;
    limit?: number;
    offset?: number;
    filter?: CourseSearchFilter;
    signal?: AbortSignal;
  }): Promise<LearningRow | null>;

  /** Learning paths ("journeys") for the current user, backing the
   *  {@code LearningJourneys} tile grid. {@code source} is a host slug
   *  ({@code assigned} | {@code all} | {@code custom}); {@code ids}
   *  narrows the result when {@code source === 'custom'}. Optional —
   *  hosts without a learning-path backend omit it and the block falls
   *  back to authored tiles. */
  fetchJourneys?(opts?: {
    source?: string;
    ids?: string[];
    limit?: number;
    signal?: AbortSignal;
  }): Promise<LearningJourney[]>;

  /** Single lean item by id — the lookup a card block resolves against
   *  when authored with an explicit course id instead of an inline item. */
  fetchItem(id: string, signal?: AbortSignal): Promise<LearningItem | null>;

  /** Rich detail for the {@code /learn/[courseId]} system page. */
  fetchCourseDetail(id: string, signal?: AbortSignal): Promise<LearningCourseDetail | null>;

  /** FAQ tab content for a course detail view. Optional — hosts without
   *  a FAQ backend simply omit this and callers hide the tab. */
  fetchCourseFaqs?(id: string, signal?: AbortSignal): Promise<LearningCourseFaq[] | null>;

  /** Documents/resources tab content for a course detail view. Optional —
   *  hosts without a document backend simply omit this and callers hide
   *  the tab. */
  fetchCourseDocuments?(id: string, signal?: AbortSignal): Promise<LearningCourseDocument[] | null>;

  /** Comments/reviews tab content for a course detail view. Optional —
   *  hosts without a comments backend simply omit this and callers hide
   *  the tab. */
  fetchCourseComments?(id: string, signal?: AbortSignal): Promise<LearningCourseComment[] | null>;

  /** Related-courses row for the detail page sidebar. */
  fetchRelatedCourses(
    courseId: string,
    limit?: number,
    signal?: AbortSignal,
  ): Promise<LearningRow | null>;

  /** Bulk catalog list with optional category filter. Used by
   *  {@code /catalog}, {@code /categories/[id]}, {@code /my-learning},
   *  sitemap generation. */
  fetchCoursesList(opts: {
    categoryId?: string;
    categoryName?: string;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<LearningItem[]>;

  /** Paginated events with status filtering. */
  fetchEventsPage(opts: {
    status?: 'upcoming' | 'ongoing' | 'past';
    type?: string;
    query?: string;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<{ items: LearningEvent[]; total: number; offset: number }>;

  /** LMS-news article list. Legacy {@code /news} surface — kept on
   *  LearningSource because the underlying records live in the LMS
   *  catalog, not the CMS. New deploys should prefer
   *  {@link BlogSource} for richer content.
   *
   *  <p>{@code authorId} narrows results to posts written by that user
   *  (MY_POSTS mode). When absent all published posts are returned
   *  (ALL_POSTS mode). */
  fetchArticles(opts: {
    category?: string;
    query?: string;
    /** Restrict to posts by this author (MY_POSTS filter). */
    authorId?: string;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<LearningArticle[]>;

  /** Single news article by id. */
  fetchArticle(id: string, signal?: AbortSignal): Promise<LearningArticle | null>;

  /** Course-categories list. Distinct from {@code BlogSource.listCategories}
   *  (these are LMS taxonomy, not CMS taxonomy). */
  fetchCategories(signal?: AbortSignal): Promise<LearningCategory[]>;

  /** Source-id → label map for the admin source dropdown. */
  listSources(signal?: AbortSignal): Promise<Record<string, string>>;

  /** Optional sub-adapter. Hosts without enrollment backing leave
   *  this undefined; consuming islands fall back gracefully. */
  enrollments?: EnrollmentSource;

  /** User's most recently active course registrations — "continue where
   *  you left off". Returns {@code null} when the host doesn't support
   *  this surface or the user is anonymous. */
  fetchCurrentCourse?(opts?: {
    limit?: number;
    signal?: AbortSignal;
  }): Promise<CurrentCourse[] | null>;

  /** Faceted, paginated catalog search backing the {@code CourseGrid}
   *  list-page block. Proxies the backend {@code catalog/search} with a
   *  {@code firstResult}/{@code maxResults}/{@code sortField}/
   *  {@code sortOrder}/{@code filter} body. Optional — hosts without a
   *  catalog-search backend omit it and the block renders an adapter
   *  placeholder. */
  searchCourses?(opts: CourseSearchOpts): Promise<CourseSearchResult>;

  /** Register the current user for a course — backs the card "Quick
   *  Start" CTA, which registers then jumps straight into the player
   *  instead of routing through the course detail page. Resolves with
   *  the backend's registration status ({@code 'REGISTERED'} |
   *  {@code 'APPROVAL_REQUIRED'} | {@code 'ALREADY_REGISTERED'} | an
   *  error code) so the caller can react without touching HTTP status
   *  codes. Optional — hosts without a registration backend omit it and
   *  cards fall back to a plain link to the course detail page. */
  registerCourse?(courseId: string, signal?: AbortSignal): Promise<{ status?: string }>;
}

const LearningSourceContext = createContext<LearningSource | null>(null);

export function LearningSourceProvider({
  value,
  children,
}: {
  value: LearningSource;
  children: ReactNode;
}) {
  return <LearningSourceContext.Provider value={value}>{children}</LearningSourceContext.Provider>;
}

/** Returns the host-provided source, or {@code null} when no
 *  provider is mounted. */
export function useLearningSource(): LearningSource | null {
  return useContext(LearningSourceContext);
}

/**
 * Heuristic discriminator for the multi-purpose {@link LearningItem}.
 * The catalog endpoint returns one envelope per source; consumers
 * call this to pick the right card/row treatment.
 */
export function detectLearningItemKind(item: LearningItem): LearningItemKind {
  if (
    item.kind === 'course' ||
    item.kind === 'article' ||
    item.kind === 'event' ||
    item.kind === 'category'
  ) {
    return item.kind;
  }
  if (item.startDate || item.applyDeadline || item.city) return 'event';
  if (item.excerpt || item.readTime) return 'article';
  if (item.description && !item.instructor && !item.duration && !item.ratingAverage)
    return 'category';
  return 'course';
}
